import { chartPeriods } from "app/charts/types";
import { ChartData } from "app/charts/BasicChart/BasicChart";
import { CHART_CANVAS_WIDTH } from "app/charts/constants";

function round(num: number) {
    return Math.round((num + Number.EPSILON) * 100) / 100;
}

function scale(domain: number[], range: number[]) {
    const m = (range[1] - range[0]) / (domain[1] - domain[0]);
    return (num: number) => round(range[0] + m * (num - domain[0]));
}

function sortChartDataByTime(a: [number, number], b: [number, number]) {
    return a[0] === b[0] ? 0 : a[0] < b[0] ? -1 : 1;
}

interface FenceObject {
    fenceStart: number;
    fenceEnd: number;
}

function calculateFence(chartData: ChartData): FenceObject {
    return {
        fenceStart: Math.min(...chartData.map(d => d[0])),
        fenceEnd: Math.max(...chartData.map(d => d[0]))
    };
}

function calculateIntradayFence(
    todayExchangeOpenTimestamp: string,
    previousExchangeCloseTimestamp: string
): FenceObject {
    const previousExchangeClose = new Date(previousExchangeCloseTimestamp);
    const todayExchangeOpen = new Date(todayExchangeOpenTimestamp);
    const todayExchangeClose = new Date(todayExchangeOpenTimestamp);

    // Open Time is across 0:00 into the next day.
    if (todayExchangeOpen.getHours() > previousExchangeClose.getHours()) {
        todayExchangeClose.setDate(todayExchangeClose.getDate() + 1);
    }

    todayExchangeClose.setHours(previousExchangeClose.getHours());
    todayExchangeClose.setMinutes(previousExchangeClose.getMinutes());
    todayExchangeClose.setSeconds(previousExchangeClose.getSeconds());

    return {
        fenceStart: todayExchangeOpen.getTime(),
        fenceEnd: todayExchangeClose.getTime()
    };
}

/**
 * Can be used for charts with values spread by time.
 */
function getIndexByTime(data: ChartData, xPercent: number, fenceStart: number, fenceEnd: number) {
    const pointedTime = (xPercent / 100) * (fenceEnd - fenceStart) + fenceStart;
    const testIndex = data.findIndex(([timeStamp]) => timeStamp > pointedTime);

    if (testIndex === -1) {
        return data.length - 1;
    }

    const testIndexDistance = Math.abs(pointedTime - data[testIndex][0]);

    if (testIndex > 1) {
        if (Math.abs(pointedTime - data[testIndex - 1][0]) < testIndexDistance) {
            return testIndex - 1;
        }
    }

    return testIndex;
}

/**
 * Can be used for uniformly spread charts. This applies to the month chart.
 */
function getIndexByPercent(data: ChartData, xPercent: number) {
    return Math.round((data.length - 1) * (xPercent / 100));
}

/**
 * This function takes a chart data array and calculates all support values for rendering a line chart.
 */
function calcValues(
    data: ChartData,
    period: chartPeriods,
    chartWidth: number,
    todayExchangeOpen?: string | null,
    lastExchangeClose?: string | null,
    lastExchangeCloseValue?: number | null
): [
    string,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    number | null,
    boolean,
    null | ((x: number, targetWidth: number, targetHeight: number) => [number, number, number, number])
] {
    const isOneMonth = period === chartPeriods.ONE_MONTH;
    let minVal = Infinity;
    let maxVal = -Infinity;
    let earliestValue = [Infinity, 0];
    let latestValue = [-Infinity, 0];

    if (!data || !data.length) {
        return ["", null, null, null, null, null, null, null, null, false, null];
    }

    data.sort(sortChartDataByTime);

    const { fenceStart, fenceEnd } =
        period === chartPeriods.INTRADAY && todayExchangeOpen && lastExchangeClose
            ? calculateIntradayFence(todayExchangeOpen, lastExchangeClose)
            : calculateFence(data);

    const xDomain = isOneMonth ? [0, data.length] : [fenceStart, fenceEnd];

    const x = scale(xDomain, [0, chartWidth]);

    const yValues = data.map(d => d[1]);
    if (period === chartPeriods.INTRADAY && lastExchangeCloseValue) {
        yValues.push(lastExchangeCloseValue);
    }
    const y = scale([Math.min(...yValues), Math.max(...yValues)], [100, 0]);

    let resultPath = "";
    let first = true;
    data.forEach(([xVal, yVal], index) => {
        minVal = Math.min(minVal, yVal);
        maxVal = Math.max(maxVal, yVal);
        if (period === chartPeriods.INTRADAY && lastExchangeCloseValue) {
            minVal = Math.min(minVal, lastExchangeCloseValue);
            maxVal = Math.max(maxVal, lastExchangeCloseValue);
        }
        if (earliestValue[0] > xVal) {
            earliestValue = [xVal, yVal];
        }
        if (latestValue[0] < xVal) {
            latestValue = [xVal, yVal];
        }
        resultPath += `${first ? "M" : "L"}${x(isOneMonth ? index : xVal)},${y(yVal)}`;
        first = false;
    });

    const avgVal = (maxVal - minVal) / 2 + minVal;
    const maxAvg = (maxVal - avgVal) / 2 + avgVal;
    const avgMin = (avgVal - minVal) / 2 + minVal;

    const risingVal = earliestValue[1] < latestValue[1];

    /**
     * xPercent is a value between 0 and 100 where 0 is the leftmost border and 100 the rightmost.
     */
    const dataGetterFunc = (xPercent: number, targetWidth: number, targetHeight: number) => {
        xPercent = Math.max(0, Math.min(100, xPercent));

        const index =
            period === chartPeriods.ONE_MONTH
                ? getIndexByPercent(data, xPercent)
                : getIndexByTime(data, xPercent, fenceStart, fenceEnd);

        const [time, value] = data[index];

        return [
            Math.round((x(isOneMonth ? index : time) / CHART_CANVAS_WIDTH) * targetWidth),
            Math.round((y(value) / 100) * targetHeight),
            value,
            time
        ] as [number, number, number, number];
    };

    let lastExchangeClosePercent = null;
    if (lastExchangeCloseValue) {
        lastExchangeClosePercent = 100 - y(lastExchangeCloseValue);
    }

    return [
        resultPath,
        minVal,
        maxVal,
        avgVal,
        maxAvg,
        avgMin,
        fenceStart,
        fenceEnd,
        lastExchangeClosePercent,
        risingVal,
        dataGetterFunc
    ];
}

export { scale, sortChartDataByTime, calcValues };
